import { Component, Vue } from 'vue-property-decorator';
import MasterDataService from '@/Services/MasterDataService';
import { ToasterService } from '@/Services/ToasterService';
import { ProcessConfig, ProcessConfigGroup, ProcessConfigPersonnelType } from '@/Domain/Entities';
import { ProcessConfigurationTypes, Constants } from '@/Domain/enum';
import { mapState, mapGetters } from 'vuex';
import i18n from '../../lang';
import _ from 'lodash';
import ProcessConfigCreator from '@/Components/ProcessConfiguration/ProcessConfigCreator/ProcessConfigCreator';
import ProcessConfigFactory from '@/Domain/Entities/ProcessConfiguration/ProcessConfigFactory';
import IProcessConfig from '@/Domain/interfaces/IProcessConfig';
import { convertBigIntToString, convertStringToBigInt } from '@/utils';
import ProcessConfigReplacementStates from '@/Components/ProcessConfiguration/ProcessConfigReplacementStates/ProcessConfigReplacementStates';
import { MdCheckbox } from '@/Components';

@Component({
    components: {
        ProcessConfigCreator,
        ProcessConfigReplacementStates,
        MdCheckbox
    },
    computed: {
        ...mapState('featureFlagsStore', ['multiLanguage']),
        ...mapState('languagesStore', { currentLanguage: 'current' }),
        ...mapGetters('languagesStore', { availableLanguages: 'getTranslatableLanguages' }),
        processesConfigOrdered() {
            return _.orderBy((this as ProcessConfiguration).processesConfig, ['isEditable', processConfig => _.deburr(processConfig.title[(this as ProcessConfiguration).currentLanguage]).toLowerCase()]);
        },
        notCurrentLanguage() {
            return (this as ProcessConfiguration).availableLanguages.find(language => language !== (this as ProcessConfiguration).currentLanguage);
        }
    }
})

export default class ProcessConfiguration extends Vue {
    currentLanguage!: string;
    availableLanguages!: string[];
    ProcessConfigurationTypes = ProcessConfigurationTypes;
    constants = Constants;
    processConfigIdToDelete: string | null = '';
    processesConfig: ProcessConfig[] = [];
    tabActiveConfigType: number = ProcessConfigurationTypes.enum.EXAMS;
    tabActiveLanguage: any = null;
    processConfigToManage: any = null;
    submittedNew = false;
    submittedEdit = false;
    loading: boolean = true;
    invalidForm: boolean = false;
    showModal: boolean = false;

    $refs!: {
        processConfigCreator: ProcessConfigCreator
    };

    editProcessConfig(editProcessConfig: ProcessConfig) {
        this.resetActiveLanguage();
        this.processConfigToManage = _.cloneDeep(editProcessConfig);
    }

    cancelEditedProcessConfig() {
        this.processConfigToManage = null;
        this.submittedEdit = false;
    }

    saveEditedProcessConfig() {
        this.submittedEdit = true;

        if (!this.processConfigToManage.title.allHasValue(this.availableLanguages)) {
            return;
        }

        // Un tipo de personal sin ningún ámbito de aplicación no se podría elegir en ningún sitio.
        if (this.processConfigToManage instanceof ProcessConfigPersonnelType && !this.processConfigToManage.hasAnyScope) {
            return;
        }

        this.loading = true;
        MasterDataService.updateProcessConfig(this.tabActiveConfigType, this.processConfigToManage)
            .then(() => {
                this.handlesUpdateProcessConfigOnSuccess();
            }).catch(() => this.loading = false);
    }

    getProcessesConfig() {
        this.loading = true;
        // Los grupos y tipos de personal ocultos también se listan (como en exámenes) para poder volver a hacerlos visibles.
        let request: Promise<any>;
        if (this.tabActiveConfigType === ProcessConfigurationTypes.enum.GROUPSUBGROUP) {
            request = MasterDataService.getProcessConfigGroupsCatalog();
        } else if (this.tabActiveConfigType === ProcessConfigurationTypes.enum.PERSONNELTYPE) {
            request = MasterDataService.getProcessConfigPersonnelTypesCatalog();
        } else {
            request = MasterDataService.getProcessesConfiguration(this.tabActiveConfigType);
        }
        request
            .then((response: IProcessConfig[]) => {
                this.processesConfig = response.map(processConfig => ProcessConfigFactory.create(
                    this.tabActiveConfigType, processConfig));
                this.setToStore();
                this.loading = false;   
            }).catch(error => console.log(error));
    }

    onTabClick(tabActive: number) {
        if (tabActive === this.tabActiveConfigType) {
            return;
        }
        this.tabActiveConfigType = tabActive;
        this.processConfigToManage = null;
        this.$refs.processConfigCreator.removeNewProcessConfig();

        if (tabActive !== ProcessConfigurationTypes.enum.REPLACEMENTSTATES) {
            this.getProcessesConfig();
        }
    }

    toggleProcessConfigVisibility() {
        if (this.processConfigToManage) {
            this.processConfigToManage.active = !this.processConfigToManage.active;
        }
    }

    // CON-451: en descuentos las opciones predeterminadas no se pueden desactivar,
    // por lo que no se renderiza el icono de visibilidad para ellas.
    // CON-441: el mismo criterio aplica a AUTHORIZATIONS (solo personalizadas son editables/desactivables)
    showVisibilityIcon(processConfig: IProcessConfig): boolean {
        if (this.tabActiveConfigType === ProcessConfigurationTypes.enum.PAYEXEMPTIONS) {
            return processConfig.isEditable;
        }

        if (this.tabActiveConfigType === ProcessConfigurationTypes.enum.AUTHORIZATIONS) {
            return processConfig.isEditable;
        }

        // CON-1229: en grupos/subgrupos los predeterminados tampoco se pueden ocultar.
        if (this.tabActiveConfigType === ProcessConfigurationTypes.enum.GROUPSUBGROUP) {
            return processConfig.isEditable;
        }

        // CON-129: en tipos de personal los predeterminados siempre son visibles; solo los personalizados se ocultan.
        if (this.tabActiveConfigType === ProcessConfigurationTypes.enum.PERSONNELTYPE) {
            return processConfig.isEditable;
        }

        return this.tabActiveConfigType === ProcessConfigurationTypes.enum.EXAMS;
    }

    // Los predeterminados (globales) no son configurables: se muestra su ámbito en solo lectura.
    personnelTypeScopeText(processConfig: ProcessConfigPersonnelType): string {
        const scopes: string[] = [];
        if (processConfig.appliesToProcesses) {
            scopes.push(i18n.t('lang.shared.personnelTypeAppliesToProcesses') as string);
        }
        if (processConfig.appliesToContracts) {
            scopes.push(i18n.t('lang.shared.personnelTypeAppliesToContracts') as string);
        }
        return scopes.join(' · ');
    }

    mounted() {
        this.getProcessesConfig();
        this.tabActiveLanguage = this.$i18n.locale;
    }

    private handlesUpdateProcessConfigOnSuccess() {
        ToasterService.showSuccess(i18n.t('lang.toaster.saveSuccess') as string);
        this.processConfigToManage = null;
        this.submittedEdit = false;
        this.getProcessesConfig();
    }

    private resetActiveLanguage() {
        this.tabActiveLanguage = this.availableLanguages[0];
    }

    private setToStore() {
        // Es el catálogo completo (incluidos los ocultos): el store lo usa para resolver los desactivados en uso.
        if (this.processesConfig[0] instanceof ProcessConfigGroup) {
            this.$store.commit('processConfigStore/setProcessConfigGroups', this.processesConfig);
        } else if (this.processesConfig[0] instanceof ProcessConfigPersonnelType) {
            this.$store.commit('processConfigStore/setProcessConfigPersonnelTypes', this.processesConfig);
        }
    }
}
